// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Invoices CRUD
// Contractor billing with auto-calculation from tickets + rates
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Table, Divider,
  Alert,
} from '../../components/gab/GabComponents';
import { DateInput } from '../../components/gab/MantineDates';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconCalculator, IconReceipt, IconFileTypePdf,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { notifications } from '../../lib/notifications';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { haversineDistance } from '../../lib/geoopsGeo';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisInvoice, DebrisType, InvoiceStatus } from '../../types/debris';

// Map debris type → primary rate schedule line item code for hauling
const TYPE_TO_LINE_ITEM: Record<string, string> = {
  vegetative: '1', c_and_d: '1', soil_mud_sand: '1',
  hazardous_tree: '18', stump: '14',
  white_goods: '12', e_waste: '12',  // map to Freon/per-UNIT line item (not per-LB)
  hhw: '11',
  mattresses: '1', tires: '1', transformers: '1',
  vehicles: '26', vessels: '26', mixed: '1',
};

// Unit-based debris types are counted per item on tickets but the FEMA rate schedule
// may price them per LB. When the rate schedule unit doesn't match the ticket measurement,
// use these per-unit fallback rates (reasonable per-appliance/item costs).
const UNIT_RATE_OVERRIDE: Record<string, number> = {
  white_goods: 75.00,   // per appliance (avg fridge/washer/dryer pickup + haul + disposal)
  e_waste: 25.00,       // per e-waste item
};

// Distance tier thresholds in km (converted from miles)
const TIER_THRESHOLDS = [24.14, 48.28, 96.56]; // 15mi, 30mi, 60mi

function getDistanceTier(pickupLat: number, pickupLng: number, siteLat: number, siteLng: number): number {
  if (!pickupLat || !pickupLng || !siteLat || !siteLng) return 0;
  const distKm = haversineDistance(pickupLat, pickupLng, siteLat, siteLng) / 1000;
  if (distKm <= TIER_THRESHOLDS[0]) return 0; // tier 1
  if (distKm <= TIER_THRESHOLDS[1]) return 1; // tier 2
  if (distKm <= TIER_THRESHOLDS[2]) return 2; // tier 3
  return 3; // tier 4
}

interface RateRow { lineItemCode: string; unit: string; baseRate: string; tier2Rate: string; tier3Rate: string; tier4Rate: string; description: string; contractorId: string; incidentId: string; }

function getRateForTier(row: RateRow, tier: number): number {
  const rates = [row.baseRate, row.tier2Rate, row.tier3Rate, row.tier4Rate];
  return parseFloat(rates[tier] || rates[0] || '0') || 0;
}

interface LineItem {
  debrisType: string;
  label: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  ticketCount: number;
  tier: string;
  lineItemCode: string;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'disputed', label: 'Disputed' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', approved: 'var(--color-success)', paid: '#03AC9E', disputed: 'var(--color-error)',
};

const EMPTY: Partial<DebrisInvoice> = {
  contractorId: '', invoiceNumber: '', periodStart: '', periodEnd: '',
  totalCubicYards: '', totalAmount: '', ticketIds: '[]', lineItems: '[]',
  status: 'draft', approvedBy: '', notes: '',
};

export default function Invoices() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    invoices, loadTickets: allTickets, loadingInvoices,
    loadInvoices, loadLoadTickets, createInvoice, updateInvoice, deleteInvoice,
  } = useDebrisOpsStore();

  const { contractors, trucks, disposalSites, loadContractors, loadTrucks, loadDisposalSites } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisInvoice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisInvoice | null>(null);
  const [form, setForm] = useState<Partial<DebrisInvoice>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [matchedTicketIds, setMatchedTicketIds] = useState<number[]>([]);
  const [rateSchedule, setRateSchedule] = useState<RateRow[]>([]);

  useEffect(() => {
    loadInvoices(); loadContractors(); loadLoadTickets(); loadTrucks(); loadDisposalSites();
    // Load rate schedules
    if (TABLES.debrisRateSchedules) {
      const FR = FIELDS.debrisRateSchedules;
      fetchTable(TABLES.debrisRateSchedules).then((rows: any[]) => {
        setRateSchedule(rows.map((r: any) => ({
          lineItemCode: String(r[FR.lineItemCode] ?? ''),
          unit: String(r[FR.unit] ?? ''),
          baseRate: String(r[FR.rateTier1] ?? ''),
          tier2Rate: String(r[FR.rateTier2] ?? ''),
          tier3Rate: String(r[FR.rateTier3] ?? ''),
          tier4Rate: String(r[FR.rateTier4] ?? ''),
          description: String(r[FR.description] ?? ''),
          contractorId: String(r[FR.contractorId] ?? ''),
          incidentId: '',
        })));
      }).catch(() => {});
    }
  }, [loadInvoices, loadContractors, loadLoadTickets, loadTrucks, loadDisposalSites]);

  // CY calculation with fallback to load% × truck capacity
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = trucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [trucks]);

  const iInvoices = incidentId ? invoices : [];
  const iContractors = incidentId ? contractors : [];
  const iTickets = allTickets;

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);

  // Define before filtered so the search filter can use it with correct deps
  const contractorName = useCallback(
    (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? (id || '-'),
    [contractors],
  );

  const filtered = useMemo(() => {
    let list = iInvoices;
    if (statusFilter && statusFilter !== 'all') list = list.filter(i => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.invoiceNumber.toLowerCase().includes(q) ||
        contractorName(i.contractorId).toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [iInvoices, statusFilter, search, contractorName]);
  const setField = (key: keyof DebrisInvoice, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Auto-calculate from tickets using rate schedule + distance tiers ──
  const calculate = useCallback(() => {
    const cId = form.contractorId;
    const start = form.periodStart;
    const end = form.periodEnd;

    if (!cId) {
      notifications.show({ message: 'Select a contractor first', color: '#C68700' });
      return;
    }

    // Get rate schedule for this contractor + incident
    const cRates = rateSchedule.filter(r => r.contractorId === cId && (!r.incidentId || r.incidentId === incidentId));
    const hasRateSchedule = cRates.length > 0;

    // Fallback to old flat rates if no rate schedule
    const contractor = contractors.find(c => String(c.id) === cId);
    if (!contractor) return;

    // Filter tickets: matching contractor, within date range, not voided
    const matched = iTickets.filter(t => {
      if (t.contractorId !== cId) return false;
      if (t.status === 'voided') return false;
      if (start && t.pickupTime && t.pickupTime < start) return false;
      if (end && t.pickupTime && t.pickupTime > end + 'Z') return false;
      return true;
    });

    // Group by debris type + distance tier
    const TIER_NAMES = ['Tier 1 (0–15mi)', 'Tier 2 (16–30mi)', 'Tier 3 (31–60mi)', 'Tier 4 (60+mi)'];
    const groupKey = (type: string, tier: number) => `${type}__${tier}`;
    const groups: Record<string, { qty: number; count: number; tier: number; type: string }> = {};

    matched.forEach(t => {
      const dt = t.debrisType || 'unknown';
      const cfg = DEBRIS_TYPES[dt as DebrisType];
      const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';

      // Compute distance tier
      const site = disposalSites.find(s => String(s.id) === t.disposalSiteId);
      const pLat = parseFloat(t.pickupLat); const pLng = parseFloat(t.pickupLng);
      const sLat = site ? parseFloat(site.latitude) : 0; const sLng = site ? parseFloat(site.longitude) : 0;
      const tier = getDistanceTier(pLat, pLng, sLat, sLng);

      // Quantity based on measurement mode
      let qty = 0;
      if (mode === 'volumetric') qty = getTicketCY(t);
      else if (mode === 'unit') qty = parseFloat(t.unitCount || '0');
      else if (mode === 'weight') qty = parseFloat(t.weight || '0');

      const key = groupKey(dt, tier);
      if (!groups[key]) groups[key] = { qty: 0, count: 0, tier, type: dt };
      groups[key].qty += qty;
      groups[key].count++;
    });

    // Build line items with rate schedule lookup
    const items: LineItem[] = Object.values(groups)
      .filter(g => g.qty > 0)
      .map(g => {
        const cfg = DEBRIS_TYPES[g.type as DebrisType];
        const mode = cfg?.measurementMode || 'volumetric';
        const unit = mode === 'volumetric' ? 'CY' : mode === 'unit' ? (cfg?.unitLabel || 'units') : (cfg?.weightUnit || 'lbs');
        const lineCode = TYPE_TO_LINE_ITEM[g.type] || '1';

        let rate = 0;
        let usedCode = lineCode;
        if (hasRateSchedule) {
          const rateRow = cRates.find(r => r.lineItemCode === lineCode);
          if (rateRow) {
            // Check if the rate schedule unit matches the ticket measurement
            const scheduleUnit = rateRow.unit?.toUpperCase() || '';
            const ticketIsUnitCount = mode === 'unit';
            const scheduleIsWeight = scheduleUnit === 'LB' || scheduleUnit === 'LBS';

            if (ticketIsUnitCount && scheduleIsWeight && UNIT_RATE_OVERRIDE[g.type]) {
              // Rate schedule is per-LB but ticket counts units — use per-unit override
              rate = UNIT_RATE_OVERRIDE[g.type];
            } else {
              rate = getRateForTier(rateRow, g.tier);
            }
            usedCode = rateRow.lineItemCode;
          } else if (UNIT_RATE_OVERRIDE[g.type] && mode === 'unit') {
            // No matching line item in schedule — use per-unit fallback
            rate = UNIT_RATE_OVERRIDE[g.type];
          }
        } else {
          // Fallback to old flat rates
          const RATE_MAP: Record<string, string> = {
            vegetative: 'rateVegetative', c_and_d: 'rateCAndD', soil_mud_sand: 'rateSoilMudSand',
            hazardous_tree: 'rateHazardousTree', stump: 'rateStumpRemoval', white_goods: 'rateWhiteGoods',
            e_waste: 'rateElectronic', hhw: 'rateHhw',
          };
          const rateField = RATE_MAP[g.type];
          rate = rateField ? parseFloat((contractor as unknown as Record<string, string>)[rateField] || '0') : 0;
        }

        return {
          debrisType: g.type,
          label: DEBRIS_TYPE_LABELS[g.type as DebrisType] ?? g.type,
          quantity: Math.round(g.qty * 10) / 10,
          unit,
          rate,
          amount: Math.round(g.qty * rate * 100) / 100,
          ticketCount: g.count,
          tier: TIER_NAMES[g.tier],
          lineItemCode: usedCode,
        };
      })
      .sort((a, b) => b.amount - a.amount);

    const totalQty = items.reduce((s, i) => s + i.quantity, 0);
    const totalAmt = items.reduce((s, i) => s + i.amount, 0);

    setLineItems(items);
    setMatchedTicketIds(matched.map(t => t.id));
    setForm(prev => ({
      ...prev,
      totalCubicYards: totalQty.toFixed(1),
      totalAmount: totalAmt.toFixed(2),
      ticketIds: JSON.stringify(matched.map(t => t.id)),
      lineItems: JSON.stringify(items),
    }));

    notifications.show({
      message: `${matched.length} tickets → ${items.length} line items${hasRateSchedule ? ' (rate schedule applied)' : ' (flat rates — no rate schedule found)'} → $${totalAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      color: hasRateSchedule ? 'var(--color-success)' : '#C68700',
    });
  }, [form.contractorId, form.periodStart, form.periodEnd, iTickets, contractors, rateSchedule, disposalSites, incidentId]);

  // Auto-generate next invoice number
  const nextInvoiceNumber = useMemo(() => {
    const year = new Date().getFullYear();
    const prefix = `INV-${year}-`;
    const existing = iInvoices
      .map(i => i.invoiceNumber)
      .filter(n => n.startsWith(prefix))
      .map(n => parseInt(n.replace(prefix, ''), 10))
      .filter(n => !isNaN(n));
    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}${String(next).padStart(3, '0')}`;
  }, [iInvoices]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, invoiceNumber: nextInvoiceNumber });
    setLineItems([]);
    setMatchedTicketIds([]);
    setModalOpen(true);
  };

  const openEdit = (inv: DebrisInvoice) => {
    setEditTarget(inv);
    setForm({ ...inv });
    // Restore line items from stored JSON
    try { setLineItems(JSON.parse(inv.lineItems || '[]')); } catch { setLineItems([]); }
    try { setMatchedTicketIds(JSON.parse(inv.ticketIds || '[]')); } catch { setMatchedTicketIds([]); }
    setModalOpen(true);
  };

  // Auto-calculate when contractor + period are set
  useEffect(() => {
    if (modalOpen && form.contractorId && form.periodStart && form.periodEnd && !editTarget) {
      calculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contractorId, form.periodStart, form.periodEnd, modalOpen]);

  const handleSave = async () => {
    if (!form.invoiceNumber?.trim()) {
      notifications.show({ message: 'Invoice number is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateInvoice(editTarget.id, payload);
        notifications.show({ message: 'Invoice updated', color: 'var(--color-success)' });
      } else {
        await createInvoice(payload);
        notifications.show({ message: 'Invoice created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteInvoice(deleteTarget.id);
      notifications.show({ message: `Invoice ${deleteTarget.invoiceNumber} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleApprove = async (inv: DebrisInvoice) => {
    try {
      await updateInvoice(inv.id, { status: 'approved', approvedBy: user?.email ?? '' });
      notifications.show({ message: `Invoice ${inv.invoiceNumber} approved`, color: 'var(--color-success)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Approve failed', color: 'var(--color-error)' });
    }
  };

  // Sanitize text for jsPDF (only supports basic Latin chars)
  const pdfSafe = (s: string) => s.replace(/[^\x20-\x7E]/g, c => {
    if (c === '\u2014' || c === '\u2013') return '-'; // em/en dash
    if (c === '\u2018' || c === '\u2019') return "'";
    if (c === '\u201C' || c === '\u201D') return '"';
    return '';
  });

  // Build line items from live ticket data for a given invoice
  const buildLineItemsFromTickets = (inv: DebrisInvoice): LineItem[] => {
    const contractor = contractors.find(c => String(c.id) === inv.contractorId);
    if (!contractor) return [];

    // Use stored ticket IDs first (exact match from when invoice was calculated)
    let storedIds: number[] = [];
    try { storedIds = JSON.parse(inv.ticketIds || '[]'); } catch { /* */ }

    let matched = storedIds.length > 0
      ? iTickets.filter(t => storedIds.includes(t.id))
      : iTickets.filter(t => {
          if (t.contractorId !== inv.contractorId) return false;
          if (t.status === 'voided') return false;
          if (inv.periodStart && t.pickupTime && t.pickupTime < inv.periodStart) return false;
          if (inv.periodEnd && t.pickupTime && t.pickupTime > inv.periodEnd + 'Z') return false;
          return true;
        });

    const byType: Record<string, { qty: number; count: number; unit: string }> = {};
    matched.forEach(t => {
      const dt = t.debrisType || 'unknown';
      const cfg = DEBRIS_TYPES[dt as DebrisType];
      const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
      if (!byType[dt]) byType[dt] = { qty: 0, count: 0, unit: mode === 'volumetric' ? 'CY' : mode === 'unit' ? (cfg?.unitLabel || 'units') : 'lbs' };
      if (mode === 'volumetric') byType[dt].qty += getTicketCY(t);
      else if (mode === 'unit') byType[dt].qty += parseFloat(t.unitCount || '0');
      else if (mode === 'weight') byType[dt].qty += parseFloat(t.weight || '0');
      byType[dt].count++;
    });

    return Object.entries(byType)
      .filter(([, v]) => v.qty > 0)
      .map(([type, v]) => ({
        debrisType: type,
        label: DEBRIS_TYPE_LABELS[type as DebrisType] ?? type,
        quantity: Math.round(v.qty * 10) / 10,
        unit: v.unit,
        rate: 0,
        amount: 0,
        ticketCount: v.count,
        tier: '',
        lineItemCode: TYPE_TO_LINE_ITEM[type] || '1',
      }))
      .sort((a, b) => b.quantity - a.quantity);
  };

  const generatePDF = (inv: DebrisInvoice) => {
    try {
      const doc = new jsPDF();
      const cName = pdfSafe(contractorName(inv.contractorId));
      // Use the stored totals from the invoice (these are authoritative — what user sees online)
      const storedTotalCY = parseFloat(inv.totalCubicYards || '0');
      const storedTotalAmt = parseFloat(inv.totalAmount || '0');

      // Try to get line item breakdown: stored JSON first, then rebuild from live data
      let items: LineItem[] = [];
      try { items = JSON.parse(inv.lineItems || '[]'); } catch { /* */ }
      // If stored items have no CY data, try rebuilding from live tickets
      const storedHasData = items.some(i => (parseFloat(String(i.quantity ?? (i as any).cubicYards)) || 0) > 0);
      if (!storedHasData && inv.contractorId) {
        const rebuilt = buildLineItemsFromTickets(inv);
        if (rebuilt.length > 0) items = rebuilt;
      }
      // Normalize all fields (backward compat: cubicYards → quantity)
      items = items.map(i => ({
        ...i,
        label: i.label || DEBRIS_TYPE_LABELS[i.debrisType as DebrisType] || i.debrisType || '',
        rate: typeof i.rate === 'number' ? i.rate : parseFloat(String(i.rate)) || 0,
        amount: typeof i.amount === 'number' ? i.amount : parseFloat(String(i.amount)) || 0,
        quantity: typeof i.quantity === 'number' ? i.quantity : (typeof (i as any).cubicYards === 'number' ? (i as any).cubicYards : parseFloat(String(i.quantity ?? (i as any).cubicYards)) || 0),
        unit: i.unit || 'CY',
        tier: i.tier || '',
        lineItemCode: i.lineItemCode || '',
        ticketCount: typeof i.ticketCount === 'number' ? i.ticketCount : parseInt(String(i.ticketCount)) || 0,
      }));
      // If line items have data, scale them to match stored totals (in case of partial ticket load)
      const itemTotalQty = items.reduce((s, i) => s + (i.quantity ?? (i as any).cubicYards ?? 0), 0);
      if (itemTotalQty > 0 && storedTotalCY > 0 && Math.abs(itemTotalQty - storedTotalCY) > 1) {
        const scale = storedTotalCY / itemTotalQty;
        items = items.map(i => ({
          ...i,
          quantity: Math.round((i.quantity ?? (i as any).cubicYards ?? 0) * scale * 10) / 10,
          amount: Math.round(i.amount * scale * 100) / 100,
        }));
      }

      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('DEBRIS REMOVAL INVOICE', 14, 22);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('OpenGov Emergency Management', 14, 29);
      doc.setTextColor(0);

      // Invoice info box
      doc.setFontSize(10);
      const infoY = 40;
      doc.setFont('helvetica', 'bold');
      doc.text('Invoice #:', 14, infoY);
      doc.text('Contractor:', 14, infoY + 7);
      doc.text('Period:', 14, infoY + 14);
      doc.text('Status:', 14, infoY + 21);
      doc.text('Incident:', 14, infoY + 28);
      doc.setFont('helvetica', 'normal');
      doc.text(pdfSafe(inv.invoiceNumber || '-'), 50, infoY);
      doc.text(cName, 50, infoY + 7);
      doc.text(`${inv.periodStart || '?'} to ${inv.periodEnd || '?'}`, 50, infoY + 14);
      doc.text(pdfSafe((inv.status || 'draft').toUpperCase()), 50, infoY + 21);
      doc.text(pdfSafe(activeIncident?.name ?? (incidentId || '-')), 50, infoY + 28);

      // Use stored totals (authoritative) with ticket count from line items
      const pdfTotalCY = storedTotalCY;
      const pdfTotalAmt = storedTotalAmt;
      const pdfTotalTickets = items.reduce((s, i) => s + i.ticketCount, 0);
      let storedTicketCount = 0;
      try { storedTicketCount = JSON.parse(inv.ticketIds || '[]').length; } catch { /* */ }
      const displayTickets = storedTicketCount > pdfTotalTickets ? storedTicketCount : pdfTotalTickets;

      // Number formatting helpers
      const fmtN = (n: number) => n.toLocaleString();
      const fmtCY = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      const fmtMon = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

      // Summary line under invoice info
      doc.setFont('helvetica', 'bold');
      doc.text(`Total: ${fmtCY(pdfTotalCY)} CY  |  ${fmtN(displayTickets)} Tickets  |  ${fmtMon(pdfTotalAmt)}`, 14, infoY + 33);
      doc.setFont('helvetica', 'normal');

      // Line items table
      if (items.length > 0) {
        autoTable(doc, {
          startY: infoY + 40,
          head: [['Debris Type', 'Distance Tier', 'Tickets', 'Quantity', 'Unit', 'Rate', 'Amount']],
          body: items.map(item => [
            pdfSafe(item.label || item.debrisType || ''),
            pdfSafe(item.tier || '-'),
            fmtN(item.ticketCount || 0),
            fmtCY(item.quantity ?? (item as any).cubicYards ?? 0),
            pdfSafe(item.unit || 'CY'),
            item.rate > 0 ? fmtMon(item.rate) : 'N/A',
            item.amount > 0 ? fmtMon(item.amount) : '-',
          ]),
          foot: [[
            'TOTAL', '',
            fmtN(displayTickets),
            fmtCY(pdfTotalCY),
            '',
            fmtMon(pdfTotalAmt),
          ]],
          headStyles: { fillColor: [44, 1, 214], textColor: 255, fontStyle: 'bold', halign: 'center' },
          footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
          styles: { fontSize: 9, halign: 'center' },
          columnStyles: {
            0: { halign: 'left' },
          },
        });
      } else {
        // No line items - just show totals from stored invoice
        const y = infoY + 40;
        doc.setFont('helvetica', 'bold');
        doc.text('Total Cubic Yards:', 14, y);
        doc.text('Total Amount:', 14, y + 8);
        doc.setFont('helvetica', 'normal');
        doc.text(fmtCY(parseFloat(inv.totalCubicYards || '0')), 70, y);
        doc.text(fmtMon(parseFloat(inv.totalAmount || '0')), 70, y + 8);
      }

      // Notes
      if (inv.notes) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const finalY = ((doc as any).lastAutoTable?.finalY as number) ?? 140;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 14, finalY + 12);
        doc.setFont('helvetica', 'normal');
        doc.text(pdfSafe(inv.notes), 14, finalY + 19, { maxWidth: 180 });
      }

      // Approval
      if (inv.approvedBy) {
        const pageH = doc.internal.pageSize.getHeight();
        doc.setFontSize(9);
        doc.text(`Approved by: ${pdfSafe(inv.approvedBy)}`, 14, pageH - 20);
      }

      // Footer
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated ${new Date().toLocaleDateString()} - OpenGov EMC`, 14, pageH - 10);

      doc.save(`Invoice-${inv.invoiceNumber || inv.id}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      notifications.show({ message: `PDF generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    }
  };

  const fmtMoney = (v: string | number) => {
    const n = typeof v === 'number' ? v : parseFloat(v || '0');
    return n > 0 ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Invoices</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingInvoices} onClick={() => loadInvoices()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Invoice</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search invoice #, contractor..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Invoice #</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Period</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Tickets</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>CY</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Breakdown</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Approved By</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingInvoices ? 'Loading...' : 'No invoices found'}
                </td></tr>
              )}
              {filtered.map(inv => {
                let ticketCount = 0;
                try { ticketCount = JSON.parse(inv.ticketIds || '[]').length; } catch { /* */ }
                // Parse or compute line items for preview
                let invItems: LineItem[] = [];
                try { invItems = JSON.parse(inv.lineItems || '[]'); } catch { /* */ }
                if (invItems.length === 0 && inv.contractorId) invItems = buildLineItemsFromTickets(inv);
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 500 }}>{inv.invoiceNumber || `#${inv.id}`}</td>
                    <td style={{ padding: '8px 16px' }}>{inv.contractorId ? contractorName(inv.contractorId) : '-'}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Text size="xs">{inv.periodStart || '?'} — {inv.periodEnd || '?'}</Text>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                      {ticketCount > 0 ? ticketCount : '-'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                      {parseFloat(inv.totalCubicYards || '0').toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney(inv.totalAmount)}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Group gap={4} wrap="wrap">
                        {invItems.slice(0, 3).map(it => (
                          <Badge key={it.debrisType} size="xs" variant="light"
                            color={DEBRIS_TYPE_COLORS[it.debrisType as DebrisType] ?? '#939598'}>
                            {it.label}: {(it.quantity ?? (it as any).cubicYards ?? 0).toLocaleString()} {it.unit || 'CY'}
                          </Badge>
                        ))}
                        {invItems.length > 3 && <Badge size="xs" variant="light" color="#939598">+{invItems.length - 3}</Badge>}
                        {invItems.length === 0 && <Text size="xs" c="dimmed">-</Text>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Badge size="sm" color={STATUS_COLORS[inv.status] ?? '#939598'}>{inv.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px' }}><Text size="xs">{inv.approvedBy || '-'}</Text></td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && inv.status === 'submitted' && (
                          <Button size="compact-xs" variant="light" color="var(--color-success)"
                            onClick={() => handleApprove(inv)}>Approve</Button>
                        )}
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                          onClick={() => generatePDF(inv)} title="Download PDF">
                          <IconFileTypePdf size={14} />
                        </ActionIcon>
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(inv)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(inv)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Invoice' : 'New Invoice'} centered size="xl">
        <Stack>
          {/* Top: contractor, dates, invoice # */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Invoice Number" required value={form.invoiceNumber ?? ''}
              onChange={e => setField('invoiceNumber', e.target.value)} />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
            <DateInput label="Period Start" clearable valueFormat="YYYY-MM-DD"
              value={form.periodStart ? new Date(form.periodStart) : null}
              onChange={(d: unknown) => setField('periodStart', d && typeof d === 'object' && 'toISOString' in d ? (d as Date).toISOString().slice(0, 10) : (d ? String(d) : ''))} />
            <DateInput label="Period End" clearable valueFormat="YYYY-MM-DD"
              value={form.periodEnd ? new Date(form.periodEnd) : null}
              onChange={(d: unknown) => setField('periodEnd', d && typeof d === 'object' && 'toISOString' in d ? (d as Date).toISOString().slice(0, 10) : (d ? String(d) : ''))} />
          </SimpleGrid>

          {/* Calculate button */}
          {canEdit && <Button leftSection={<IconCalculator size={16} />} variant="light" color="var(--data-viz-650)"
            onClick={calculate}>
            Calculate from Tickets
          </Button>}

          {/* Line items breakdown */}
          {lineItems.length > 0 && (
            <Paper p="sm" radius="md" withBorder style={{ background: '#F8F8F8' }}>
              <Group gap="xs" mb="xs">
                <IconReceipt size={16} />
                <Text size="sm" fw={700}>Line Items ({matchedTicketIds.length} tickets)</Text>
              </Group>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Debris Type</Table.Th>
                    <Table.Th>Distance Tier</Table.Th>
                    <Table.Th ta="right">Tickets</Table.Th>
                    <Table.Th ta="right">Quantity</Table.Th>
                    <Table.Th ta="right">Unit</Table.Th>
                    <Table.Th ta="right">Rate</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {lineItems.map((item, idx) => (
                    <Table.Tr key={`${item.debrisType}-${idx}`}>
                      <Table.Td>
                        <Group gap="xs">
                          <div style={{
                            width: 10, height: 10, borderRadius: 4,
                            backgroundColor: DEBRIS_TYPE_COLORS[item.debrisType as DebrisType] ?? '#6b7280',
                          }} />
                          <Text size="sm">{item.label}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{item.tier || '-'}</Badge></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{item.ticketCount}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace">{item.quantity.toLocaleString()}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" c="dimmed">{item.unit}</Text></Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" ff="monospace">
                          {item.rate > 0 ? `$${item.rate.toFixed(2)}` : <Text span c="var(--color-error)" size="sm">no rate</Text>}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={600} ff="monospace">{fmtMoney(item.amount)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
                <Table.Tfoot>
                  <Table.Tr style={{ borderTop: '2px solid #939598' }}>
                    <Table.Td><Text size="sm" fw={700}>Total</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{matchedTicketIds.length}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={700} ff="monospace">
                        {lineItems.reduce((s, i) => s + i.quantity, 0).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td />
                    <Table.Td ta="right">
                      <Text size="sm" fw={700} ff="monospace" c="var(--color-success)">
                        {fmtMoney(lineItems.reduce((s, i) => s + i.amount, 0))}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tfoot>
              </Table>
              {lineItems.some(i => i.rate === 0 && i.quantity > 0) && (
                <Alert color="#C68700" mt="xs" p="xs">
                  <Text size="xs">Some debris types have no rate set on this contractor. Edit contractor rates to include them in the total.</Text>
                </Alert>
              )}
            </Paper>
          )}

          <Divider />

          {/* Totals (editable for manual override) */}
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Total Cubic Yards" value={form.totalCubicYards ?? ''}
              onChange={e => setField('totalCubicYards', e.target.value)}
              description={lineItems.length > 0 ? 'Auto-calculated' : undefined} />
            <TextInput label="Total Amount ($)" value={form.totalAmount ?? ''}
              onChange={e => setField('totalAmount', e.target.value)}
              description={lineItems.length > 0 ? 'Auto-calculated' : undefined} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as InvoiceStatus) ?? 'draft'}
              onChange={v => setField('status', v ?? 'draft')} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Invoice'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Invoice" centered size="sm">
        <Stack>
          <Text size="sm">Delete invoice <strong>{deleteTarget?.invoiceNumber}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
